import { useQuery } from "@tanstack/react-query";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import api from "@/lib/api";
import { PageHeader, StatCard, Table } from "@/admin/ui";

export default function Analytics() {
  const { data } = useQuery({
    queryKey: ["admin-analytics"],
    queryFn: async () => (await api.get("/admin/analytics/overview")).data,
  });

  if (!data) return <div className="text-gray-500 py-10">Loading analytics...</div>;
  const totals = data.totals || {};

  return (
    <div data-testid="admin-analytics-page">
      <PageHeader title="Analytics" sub="Traffic, engagement, and lead capture performance" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <StatCard label="Page views" value={totals.page_view || 0} />
        <StatCard label="Article views" value={totals.article_view || 0} accent="#7C3AED" />
        <StatCard label="Newsletter signups" value={(totals.newsletter_signup || 0)} accent="#FFD600" />
        <StatCard label="Searches" value={totals.search || 0} accent="#10B981" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <h3 className="font-heading font-semibold text-white text-sm mb-4">14-day traffic</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.series}>
              <XAxis dataKey="day" tick={{ fill: "#6B7280", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={30} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <Area type="monotone" dataKey="page_views" stroke="#00E5FF" fill="#00E5FF22" name="Page views" />
              <Area type="monotone" dataKey="article_views" stroke="#7C3AED" fill="#7C3AED22" name="Article views" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#121212] border border-white/10 rounded-lg p-5">
          <h3 className="font-heading font-semibold text-white text-sm mb-4">14-day signups & searches</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.series}>
              <XAxis dataKey="day" tick={{ fill: "#6B7280", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} width={30} />
              <Tooltip contentStyle={{ background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", color: "#fff" }} />
              <Bar dataKey="signups" fill="#FFD600" name="Signups" />
              <Bar dataKey="searches" fill="#10B981" name="Searches" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section>
          <h3 className="font-heading font-semibold text-white text-sm mb-3">Category performance</h3>
          <Table headers={["Category", "Articles", "Views"]}>
            {(data.categories || []).map((c: any) => (
              <tr key={c.category}>
                <td className="px-4 py-2.5 text-white">{c.category}</td>
                <td className="px-4 py-2.5 font-mono text-gray-400">{c.articles}</td>
                <td className="px-4 py-2.5 font-mono text-[#00E5FF]">{c.views}</td>
              </tr>
            ))}
          </Table>
        </section>
        <section>
          <h3 className="font-heading font-semibold text-white text-sm mb-3">Top content</h3>
          <Table headers={["Title", "Views"]}>
            {(data.top_articles || []).map((a: any) => (
              <tr key={a.id}>
                <td className="px-4 py-2.5 text-white max-w-[320px] truncate">{a.title}</td>
                <td className="px-4 py-2.5 font-mono text-[#00E5FF]">{a.views}</td>
              </tr>
            ))}
          </Table>
        </section>
      </div>
    </div>
  );
}
